/*
 * Copyright © 2024 Himitsu Lab Limited. All Rights Reserved.
 */

/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Icon from '../../Components/base/icon/icon';
import { Button } from '../../Components';
import { useTermsAndConditionsHooks } from '../../Hooks/TermsAndConditionsHook';
import Footer from '../Footer';
import { useAppSelector } from '../../Store/hooks';
import { getCurrentUser } from '../../Services/userReducer';
import {
  changeTermsAndCondition,
  getIsSignUpTermsAccepted,
} from '../../Services/signUpReducer';
import { useDispatch } from 'react-redux';
import { useUpdateUserTermsPolicyMutation } from '../../Services/userPolicyAgreementsApi';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { toastSuccess } from '../../Components/toast';
import Header from '../Header';
import Field from '../../Components/base/field/field';
import { getToken } from '../../Services/tokenReducer';
import {
  Accordion,
  AccordionItem,
} from '../../Components/base/accordion/accordion';
import { EventCard } from '../EventsCard/EventCard';
import { format } from 'date-fns';
import NewIcon from '../../Components/base/icon/newIcons';
import beemg_arrow from '../../Assets/icons/beemg-arrow.svg';
import { Verified } from '../../models/user.model';
import { printContent } from '../../Components/base/print-section/printContent';
import { markdownToHtmlWithHighlight } from '../../Components/markdownToHtmlWithHighlight';
import Loading from '../../Components/base/loading/loading';

// Types for better type safety
interface SearchResults {
  highlightedMainPolicyDescription: string;
  updatedPolicies: Array<{
    highlightedName: string;
    highlightedDescription: string;
    [key: string]: any;
  }>;
}

/**
 * A component to display the terms and conditions of the application.
 * Optimized for performance with memoization and efficient state management.
 */
function TermsAndConditions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userAccessToken = useAppSelector(getToken);
  const currentUser = useAppSelector(getCurrentUser);
  const {
    hidebutton,
    policies,
    navigateScreen,
    filteredPolicies, // Now receiving pre-filtered policies from hook
  } = useTermsAndConditionsHooks();
  
  const [
    agreeTermsAndPolicies,
    { isSuccess: userPolicyAgreed, isLoading: userPolicyLoading },
  ] = useUpdateUserTermsPolicyMutation();
  
  const termsAndConditionStatus = useAppSelector(getIsSignUpTermsAccepted);
  const [toasterView, setToasterView] = useState(false);
  const accordionRef = useRef<HTMLDivElement | null>(null);
  const policyRefs = useRef<HTMLDivElement[]>([]);
  
  // Initialize checkboxes state based on filteredPolicies length
  const [checkboxes, setCheckboxes] = useState<boolean[]>(() => 
    filteredPolicies ? new Array(filteredPolicies.length).fill(false) : []
  );
  
  const [subTermsContentVisible, setSubTermsContentVisible] = useState<boolean[]>(() =>
    filteredPolicies ? new Array(filteredPolicies.length).fill(false) : []
  );
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResults>({
    highlightedMainPolicyDescription: '',
    updatedPolicies: [],
  });

  // Memoize computed values to prevent unnecessary recalculations
  const isAllChecked = useMemo(() => {
    return checkboxes.length > 0 && 
           checkboxes.length === filteredPolicies?.length && 
           checkboxes.every(checkbox => checkbox === true);
  }, [checkboxes, filteredPolicies?.length]);

  const formattedDate = useMemo(() => {
    return policies?.updatedAt ? format(new Date(policies.updatedAt), "dd MMMM yyyy") : '';
  }, [policies?.updatedAt]);

  // Debounced search to improve performance
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      const cleanedTerm = term.trim();
      
      if (!cleanedTerm) {
        setSearchResults({
          highlightedMainPolicyDescription: '',
          updatedPolicies: [],
        });
        return;
      }

      setSearchResults({
        highlightedMainPolicyDescription: markdownToHtmlWithHighlight(policies?.description || "", cleanedTerm),
        updatedPolicies: filteredPolicies?.map((policy) => ({
          ...policy,
          highlightedName: markdownToHtmlWithHighlight(policy.policyName || "", cleanedTerm),
          highlightedDescription: markdownToHtmlWithHighlight(policy.description || "", cleanedTerm),
        })) || [],
      });
    }, 300),
    [policies?.description, filteredPolicies]
  );

  // Handle search input
  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    debouncedSearch(term);
  }, [debouncedSearch]);

  // Optimized checkbox change handler
  const handleCheckboxChange = useCallback((index: number, checked: boolean) => {
    setCheckboxes(prev => {
      const newCheckboxes = [...prev];
      newCheckboxes[index] = checked;
      return newCheckboxes;
    });
  }, []);

  // Optimized toggle function
  const togglePolicyDescription = useCallback((index: number) => {
    setSubTermsContentVisible(prev =>
      prev.map((visible, i) => (i === index ? !visible : visible)),
    );
  }, []);

  // Print functions with memoization
  const printMainContent = useCallback(() => {
    printContent(`[data-content-index="mainContent"]`, {
      title: 'BeeMG Policy',
      header: t('termsAndConditions'),
    });
  }, [t]);

  const printAccordionContent = useCallback((index: number) => {
    if (filteredPolicies?.[index]) {
      printContent(`[data-content-index="${index}"]`, {
        title: 'BeeMG Policy',
        header: filteredPolicies[index].policyName,
      });
    }
  }, [filteredPolicies]);

  // Navigation effect
  useEffect(() => {
    if (navigateScreen === 'NAVIGATE_TO_LOGIN') {
      navigate('/signIn');
    }
  }, [navigateScreen, navigate]);

  // Initialize arrays when filteredPolicies changes
  useEffect(() => {
    if (filteredPolicies && filteredPolicies.length > 0) {
      const length = filteredPolicies.length;
      setCheckboxes(prev => prev.length === length ? prev : new Array(length).fill(false));
      setSubTermsContentVisible(prev => prev.length === length ? prev : new Array(length).fill(false));
    }
  }, [filteredPolicies?.length]);

  // Terms acceptance effect
  useEffect(() => {
    if (termsAndConditionStatus && toasterView) {
      toastSuccess(t('termsAndConditionsAcceptedSuccessfully'));
      navigate('/s/creator');
    }
  }, [termsAndConditionStatus, toasterView, t, navigate]);

  // User policy agreement effect
  useEffect(() => {
    const updateCategoriesIfNeededAndNavigate = async () => {
      if (
        currentUser.userCategory?.length === 0 &&
        (currentUser.faceVerified === Verified.Complete || currentUser.faceVerified === Verified.Skipped)
      ) {
        try {
          navigate('/s/chooseInterests');
        } catch (error) {
          console.error('Failed to update categories:', error);
        }
      } else {
        navigate('/s/creator');
      }
    };

    if (userPolicyAgreed) {
      dispatch(changeTermsAndCondition(true));
      toastSuccess(t('termsAndConditionsUpdatedSuccessfully'));
      updateCategoriesIfNeededAndNavigate();
    }
  }, [userPolicyAgreed, dispatch, t, navigate, currentUser]);

  // Handle accept button click
  const handleAcceptClick = useCallback(() => {
    if (currentUser && userAccessToken) {
      agreeTermsAndPolicies(currentUser);
    } else {
      dispatch(changeTermsAndCondition(true));
      setToasterView(true);
    }
  }, [currentUser, userAccessToken, agreeTermsAndPolicies, dispatch]);

  // Handle cancel button click
  const handleCancelClick = useCallback(() => {
    navigate('/intro/choosePreference');
  }, [navigate]);

  if (userPolicyLoading) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col h-screen font-['Sora']">
      <div className="flex flex-col h-screen">
        <div
          className={`fixed ${currentUser ? "top-25" : "top-0"
            } left-0 w-full z-20 px-2 py-0`}
        >
          {!currentUser && <Header />}
          <div className="fixed top-20 left-0 w-full z-20 px-2 py-0 overflow-auto bg-white">
            {/* Title and Search Field */}
            <div className="flex flex-wrap items-center justify-between w-full mt-0">
              <div 
                id="txt_termsAndConditions" 
                className="text-xl md:text-base font-semibold flex-grow text-center ml-0 md:ml-60 font-['Sora']"
              >
                {t('termsAndConditions')}
              </div>
              <div className="w-full md:w-64 mt-4 md:mt-1 mr-0">
                <Field
                  name="policyName"
                  type="text"
                  data-testid="searchByName"
                  placeholder={t("searchByName")}
                  icon={<NewIcon icon="SEARCH_GLASS" stroke="#535353" />}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleSearch(e.target.value)
                  }
                  className="font-['Sora']"
                />
              </div>
            </div>

            {/* Last Updated Section */}
            <div className="flex flex-col items-center justify-center space-y-2 px-4 py-2 min-h-[50px]">
              <p 
                id="txt_lastUpdated" 
                className="text-base whitespace-nowrap font-['Sora']"
              >
                {t("lastUpdatedEffectiveDate")}
                {": "}
                {formattedDate}
              </p>
              <p className="text-base whitespace-nowrap font-['Sora']">
                {t("version")}: {policies?.policyVersion}
              </p>
            </div>

            {/* Marketplace Agreement */}
            <div className="text-center font-semibold text-sm mt-1 px-4 whitespace-nowrap font-['Sora']">
              {t("theSiteIsAMarketplaceForServicesAndByUsingItYouAgreeToTheseTermsAndConditions")}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`flex-grow flex-1 overflow-y-auto ${
          currentUser ? "mt-36 sm:mt-40 md:mt-44 lg:mt-48" : "mt-48 sm:mt-52 md:mt-56 lg:mt-60"
        } overflow-x-hidden min-h-[85vh] list-inherited`}
      >
        <div className="w-full flex flex-col items-center overflow-y-auto overflow-x-hidden justify-center">
          <div className="mb-8 gap-2 w-full lg:w-9/12 flex flex-col place-items-center">
            <div className="w-full mt-14 items-center">
              <div className="flex flex-col">
                {/* Policy Description */}
                <div
                  id="policy-content-scroll"
                  className="relative max-h-80 overflow-y-auto overflow-x-hidden bg-white border-r-0 mx-[5%] lg:mx-[10%]"
                >
                  {/* Main Content Print Icon */}
                  <div className="absolute top-4 right-4 z-10">
                    <button
                      className="p-2 rounded focus:outline-none hover:bg-gray-200"
                      onClick={printMainContent}
                      aria-label="Print main content"
                    >
                      <Icon icon="PRINT" size="small" stroke="#535353" />
                    </button>
                  </div>
                  <div 
                    data-content-index="mainContent" 
                    className="flex-row mx-[4%] justify-content policy-list-style font-['Sora'] text-justify leading-relaxed space-y-4"
                  >
                    <span
                      className="text-base"
                      dangerouslySetInnerHTML={{
                        __html: searchResults.highlightedMainPolicyDescription || 
                               markdownToHtmlWithHighlight(policies?.description || '', searchTerm),
                      }}
                    />
                  </div>
                </div>

                {/* Accordion Section - Only mapping logic here */}
                <div className="mt-6 mx-[5%] lg:mx-[10%] h-auto" ref={accordionRef}>
                  {filteredPolicies &&
                    filteredPolicies.map((policy, index) => (
                      <Accordion key={`acc-${index}`}>
                        <div
                          ref={(el) => {
                            if (el) {
                              policyRefs.current[index] = el;
                            }
                          }}
                          data-testid={`policyCard-${index}`}
                          id={`policyCard-${index}`}
                          className="border border-gray-200 rounded-2xl w-full mb-2"
                        >
                          <AccordionItem toggle={`accint-${index}`} color="card">
                            <EventCard keyId={index}>
                              <EventCard.Image>
                                <input
                                  type="checkbox"
                                  id="acceptTerms"
                                  className="rounded-md focus:outline-none focus:ring-2 focus:ring-white"
                                  name={`acceptTerms-${index}`}
                                  checked={checkboxes[index] || false}
                                  onChange={(e) =>
                                    handleCheckboxChange(index, e.target.checked)
                                  }
                                  aria-label={`Accept ${policy.policyName}`}
                                />
                              </EventCard.Image>
                              <EventCard.Title>
                                <span
                                  data-testid={`txt_title${index}`}
                                  id={`txt_title${index}`}
                                  className="text-base truncate mt-1 font-['Sora']"
                                  dangerouslySetInnerHTML={{
                                    __html:
                                      searchResults.updatedPolicies?.[index]?.highlightedName ??
                                      policy.policyName ?? "",
                                  }}
                                />
                              </EventCard.Title>

                              <EventCard.Actions>
                                <button
                                  onClick={() => togglePolicyDescription(index)}
                                  className="p-2 rounded focus:outline-none hover:bg-gray-200"
                                  aria-label={`Toggle ${policy.policyName} description`}
                                >
                                  <img 
                                    src={beemg_arrow} 
                                    alt="Toggle" 
                                    width={15} 
                                    height={15} 
                                    className={`transition-transform duration-300 ${
                                      subTermsContentVisible[index] ? "rotate-90" : "rotate-0"
                                    }`} 
                                  />
                                </button>
                              </EventCard.Actions>
                            </EventCard>
                          </AccordionItem>
                          {subTermsContentVisible[index] && (
                            <div 
                              id="policy-content-scroll" 
                              className="relative max-h-60 overflow-y-auto overflow-x-hidden p-4 text-sm"
                            >
                              {/* Print Icon for Accordion Content */}
                              <button
                                className="absolute mx-[3%] text-right top-2 right-2 focus:outline-none hover:bg-gray-200"
                                onClick={() => printAccordionContent(index)}
                                aria-label={`Print ${policy.policyName}`}
                              >
                                <Icon icon="PRINT" size="small" stroke="#535353" />
                              </button>

                              {/* Accordion Content */}
                              <div
                                data-content-index={`${index}`}
                                className="p-4 font-['Sora'] text-justify leading-relaxed space-y-4"
                              >
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: searchResults.updatedPolicies?.[index]?.highlightedDescription ||
                                           markdownToHtmlWithHighlight(policy.description || '', searchTerm),
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </Accordion>
                    ))}
                  
                  {/* Buttons Section */}
                  <div className="flex">
                    <span className="w-full flex-wrap flex gap-2 mt-3 justify-end">
                      {!hidebutton && (
                        <>
                          <Button
                            data-testid="btn_cancel"
                            id="btn_cancel"
                            className="w-full md:w-64 normal-case font-['Sora']"
                            onClick={handleCancelClick}
                          >
                            {t("cancel")}
                          </Button>

                          <Button
                            data-testid="btn_accept"
                            id="btn_acceptTerms"
                            className="w-full md:w-64 normal-case font-['Sora']"
                            disabled={!isAllChecked || userPolicyLoading}
                            onClick={handleAcceptClick}
                          >
                            {userPolicyLoading ? t("processing...") : t("acceptAndContinue")}
                          </Button>
                        </>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!currentUser && <Footer />}
    </div>
  );
}

// Utility function for debouncing
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export default TermsAndConditions;